/**
 * PROJECT PIPELINE (guided journey) — the deterministic mapping from ledger state to "where am I in
 * the pipeline?", so the room can render a stepper (Plan → Review → Finalize → Run) with the current
 * stage highlighted. Pure + testable; derived from the SAME `/tasks` dimensions the composer's
 * next-step proposal uses, so the stepper and the suggested action can never disagree
 * (PROJECT-NETWORKS.md §8 + the 5-stage pipeline). No LLM.
 */

// The user-facing journey for ONE milestone (it loops per milestone as the cursor advances).
export const PIPELINE_STAGES = [
  { key: 'plan', label: 'Plan', hint: 'Describe the milestone + attach artifacts; Send drafts the plan.' },
  { key: 'review', label: 'Review', hint: 'Review the drafted plan + fix any handoff breaks, then Approve.' },
  // key stays 'finalize' (stable id); the user-facing step is LOCK (P5a, per milestone).
  { key: 'finalize', label: 'Lock', hint: 'Lock the approved milestone in the Tasks tab — its tasks become runnable and its schedule is saved.' },
  { key: 'run', label: 'Run', hint: 'Run the ready tasks; agents execute and are judged against each task’s DoD.' },
];

/**
 * Which stage is ACTIVE for the current (cursor) milestone.
 * @param {{finalized?:boolean, planningCursor?:string, approvedMilestones?:string[], readyTasks?:any[]}} planState
 * @param {{id:string,name:string}|null} approvableMilestone  the milestone whose plan is complete but unapproved
 * @returns {'plan'|'review'|'finalize'|'run'}
 */
export function deriveProjectStage(planState, approvableMilestone) {
  const s = planState || {};
  const cursor = String(s.planningCursor || '');
  const cursorApproved = !!cursor && (s.approvedMilestones || []).includes(cursor);
  const ready = (s.readyTasks || []).length > 0;
  const finalized = !!s.finalized;

  // Priority mirrors the composer's next-step proposal: awaiting-approval wins, then the
  // approved-but-not-finalized gate, then still-planning, then execution.
  if (approvableMilestone) return 'review';        // plan drafted → needs the human Approve gate
  if (!finalized && cursorApproved) return 'finalize'; // approved, tasks not yet opened for run
  if (!finalized) return 'plan';                   // drafts still being authored/refined
  if (ready) return 'run';                         // finalized + runnable tasks
  return 'run';                                    // finalized, nothing ready → running / milestone done
}

/** The ordered stages annotated with `state` ∈ {done, active, todo} for a stepper. */
export function pipelineStepsFor(activeKey) {
  const idx = PIPELINE_STAGES.findIndex((st) => st.key === activeKey);
  const at = idx < 0 ? 0 : idx;
  return PIPELINE_STAGES.map((st, i) => ({
    ...st,
    state: i < at ? 'done' : i === at ? 'active' : 'todo',
  }));
}

/**
 * RUNNABLE (P5a): may Task/Wave runs start? The legacy whole-plan ``finalized`` flag OR any milestone
 * LOCKED. A lock opens THAT milestone for execution while others are still being planned — the flag
 * alone stays false until no draft remains anywhere, so a locked milestone could never be run.
 * @param {{finalized?:boolean, milestones?:Array<{locked?:boolean}>}} tasksPayload  the GET /tasks body
 */
export function isPlanRunnable(tasksPayload) {
  const d = tasksPayload || {};
  return !!d.finalized || (Array.isArray(d.milestones) && d.milestones.some((m) => !!(m && m.locked)));
}

/**
 * The run body's TASK BINDING fields (Unit F + G0b): 'task' / 'verify' bind ONE task (``taskId`` or ''
 * = the next ready), 'wave' the whole ready set. Never sent with planMode. (The room dropped the id
 * for anything but 'task' — a Verify run would have checked the wrong task.)
 */
export function taskBindingFields(planMode, taskBinding, taskId, taskIds = []) {
  if (planMode || !taskBinding) return {};
  if (taskBinding === 'tasks') {   // RUN TASKS: the selection's ids (the runner orders + continues)
    return { taskBinding, taskIds: [...new Set((taskIds || []).map(String).filter(Boolean))] };
  }
  return { taskBinding, ...((taskBinding === 'task' || taskBinding === 'verify') && taskId ? { taskId } : {}) };
}

/** RUN TASKS (MODES.md §2, M3): the selection in dependency order — LAYERS of task ids, a layer = what can
 *  run together (each dependency INSIDE the selection sits in an earlier layer; a dependency outside it is
 *  the server's to judge). Kahn over the selection; a cycle's leftovers form a last layer. Pure. */
export function runLayers(selectedIds = [], tasks = []) {
  const want = [...new Set((selectedIds || []).map(String).filter(Boolean))];
  const inSel = new Set(want);
  const byId = new Map((tasks || []).filter((t) => t && t.id).map((t) => [String(t.id), t]));
  const deps = new Map(want.map((id) => [id,
    new Set(((byId.get(id) || {}).dependsOn || []).map(String).filter((d) => inSel.has(d) && d !== id))]));
  const layers = [];
  const placed = new Set();
  while (placed.size < want.length) {
    const layer = want.filter((id) => !placed.has(id) && [...deps.get(id)].every((d) => placed.has(d)));
    if (!layer.length) { layers.push(want.filter((id) => !placed.has(id))); break; }
    layer.forEach((id) => placed.add(id));
    layers.push(layer);
  }
  return layers;
}

/**
 * RUN ▾ PREFILL (MODES.md §2): the prompt text a Run choice fills in — the task ids people + agents read.
 *  task → ``Run M0-001.2 — Draft the SoW`` (the picked task, else the next ready one)
 *  wave → ``Run wave 2 of SCOPING — M0-001.3, M0-001.4, …`` (every ready task; ``waves 1–2`` when mixed)
 *  tasks → ``Run M1-004 → M1-005 + M1-006 → M1-007`` (the selection's layers; ``+`` = together)
 * Returns '' when nothing is ready. Pure.
 */
export function runPrefill(style, { readyList = [], selectedTaskId = '', milestone = '', runSelection = null } = {}) {
  if (style === 'tasks') {
    const layers = (runSelection && runSelection.layers) || [];
    if (!layers.length) return '';
    return `Run ${layers.map((l) => l.join(' + ')).join(' → ')}`;
  }
  const ready = (readyList || []).filter((t) => t && t.id);
  if (!ready.length) return '';
  if (style === 'wave') {
    const ws = [...new Set(ready.map((t) => Number(t.wave) || 0).filter((w) => w > 0))].sort((a, b) => a - b);
    const label = ws.length === 1 ? `wave ${ws[0]}` : ws.length > 1 ? `waves ${ws[0]}–${ws[ws.length - 1]}` : 'the ready tasks';
    const ids = ready.slice(0, 8).map((t) => t.id).join(', ') + (ready.length > 8 ? `, +${ready.length - 8} more` : '');
    return `Run ${label}${milestone ? ` of ${milestone}` : ''} — ${ids}`;
  }
  const t = ready.find((x) => x.id === selectedTaskId) || ready[0];
  return `Run ${t.id}${t.title ? ` — ${t.title}` : ''}`;
}

/** Refill the prompt for a new Run choice, keeping the user's own notes: the old prefill (if the prompt
 *  starts with it) is swapped for the new one; a prompt the user rewrote is left alone. Pure. */
export function refillPrompt(prompt, oldPrefill, newPrefill) {
  const p = String(prompt || '');
  if (!p.trim()) return newPrefill;
  if (oldPrefill && p.startsWith(oldPrefill)) return newPrefill + p.slice(oldPrefill.length);
  return p;
}


/** THE ROOM'S JOURNEY DEFAULT (MODES.md §7) — the mode a room is in until someone sets it, computed from SHARED
 *  plan data so every person gets the same: something runnable → ``run``; a plan someone has WORKED (a task a plan
 *  run authored, a task past draft/pending, an approved milestone, a planner-added task) → ``plan``; otherwise —
 *  nothing, or only the untouched template skeleton a new room is seeded with → ``context``. Pure. */
// How a new room's SKELETON is stamped (``Origin`` falls back to the seeding ``set_by``): the template, the
// playbook a new room inherits, the concretion pass. Anything else (planner-ui, reconciler, …) is a task someone added.
// …and the network's SUGGESTED plan a room adopted untouched (owner 2026-10-21).
export const SKELETON_ORIGINS = new Set(['', 'template', 'playbook-inherit', 'concretion', 'suggested']);

export function hasWorkedPlan(planState = {}) {
  if ((planState.approvedMilestones || []).length) return true;
  return (planState.allTasks || []).some((t) => t && (
    t.authoredRunId
    || !SKELETON_ORIGINS.has(String(t.origin || ''))
    || (t.status && !['draft', 'pending'].includes(String(t.status)))));
}

export function journeyModeFor(planState = {}) {
  if (planState.finalized && (planState.readyTasks || []).length) return 'run';
  return hasWorkedPlan(planState) ? 'plan' : 'context';
}

/** THE PLAN TABS (Tasks · Project map — MODES.md §7): shown ONLY once the room's mode is KNOWN to be Plan / Run /
 *  Verify (never render-then-remove), for the room the decision was made for. ``decided`` = ``{room, on}`` (set when
 *  the mode is known); ``roomKey`` = the room on screen. A Context-only room never shows them. Pure. */
export function planTabsDecision({ isProject, contextOnly, mode }) {
  return !!isProject && !contextOnly && !!mode && mode !== 'context';
}

export function planTabsShown(decided, roomKey) {
  return !!(decided && decided.on && decided.room && decided.room === roomKey);
}
